import { useState, useEffect, useMemo } from 'react';
import {
  Map as MapIcon, Plus, ArrowLeft, Trash2, Calendar, CheckCircle2, Circle,
  Loader2, Sparkles, RefreshCw, ChevronRight, AlertTriangle, Target, Users,
  Megaphone, ShieldCheck, Handshake, Coins, Wallet, Layers, BookOpen,
  ChevronDown, ChevronUp, Zap, Info, Clock,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  createRoadmap, listRoadmaps, updateRoadmap, deleteRoadmap, previewRoadmap,
  toggleRoadmapMilestone,
} from '../services/api';
import { InfoTip } from '../components/ui';

// ─── Constants ───────────────────────────────────────────────────────────────

const PATTERNS = [
  { id: 'linear',      label: 'Linear',      desc: 'Steady change over time' },
  { id: 'scurve',      label: 'S-Curve',     desc: 'Slow start, fast middle, slow finish' },
  { id: 'exponential', label: 'Exponential', desc: 'Accelerating ramp' },
];

const FOCUS_AREAS = {
  community:    { label: 'Community',    icon: Users,        color: 'violet' },
  product:      { label: 'Product',      icon: Layers,       color: 'cyan' },
  marketing:    { label: 'Marketing',    icon: Megaphone,    color: 'amber' },
  compliance:   { label: 'Compliance',   icon: ShieldCheck,  color: 'blue' },
  partnerships: { label: 'Partnerships', icon: Handshake,    color: 'pink' },
  tokenomics:   { label: 'Tokenomics',   icon: Coins,        color: 'emerald' },
  treasury:     { label: 'Treasury',     icon: Wallet,       color: 'orange' },
};

const TEMPLATES = [
  {
    id: 'utility_launch',
    name: 'Utility Token Launch',
    description: 'For platform/product utility tokens — adoption over hype',
    icon: Zap,
    accent: 'cyan',
    phases: [
      {
        name: 'Product Readiness', days: 10, intensityStart: 60, intensityEnd: 90, pattern: 'scurve', focusArea: 'product',
        milestones: [
          { title: 'Smart contract audit completed',         dueOffsetDays: 3, completed: false, owner: 'Engineering' },
          { title: 'Token utility documented in product',    dueOffsetDays: 5, completed: false, owner: 'Product' },
          { title: 'In-app token features feature-flagged',  dueOffsetDays: 7, completed: false, owner: 'Engineering' },
          { title: 'Tokenomics whitepaper published',        dueOffsetDays: 9, completed: false, owner: 'Founders' },
        ],
      },
      {
        name: 'Closed Beta', days: 10, intensityStart: 70, intensityEnd: 80, pattern: 'linear', focusArea: 'community',
        milestones: [
          { title: 'Beta cohort invited (top users)',        dueOffsetDays: 0, completed: false, owner: 'Community' },
          { title: 'First on-product token usage measured',  dueOffsetDays: 3, completed: false, owner: 'Product' },
          { title: 'Feedback loop closed (1-on-1s w/ beta)', dueOffsetDays: 7, completed: false, owner: 'Founders' },
          { title: 'Public launch readiness review',         dueOffsetDays: 9, completed: false, owner: 'Founders' },
        ],
      },
      {
        name: 'Public Launch', days: 10, intensityStart: 100, intensityEnd: 70, pattern: 'linear', focusArea: 'marketing',
        milestones: [
          { title: 'TGE: Token Generation Event live',       dueOffsetDays: 0, completed: false, owner: 'Engineering' },
          { title: 'DEX liquidity deployed (Uniswap V3)',    dueOffsetDays: 0, completed: false, owner: 'Treasury' },
          { title: 'In-product token flows enabled for all', dueOffsetDays: 1, completed: false, owner: 'Engineering' },
          { title: 'Launch AMA hosted',                      dueOffsetDays: 2, completed: false, owner: 'Founders' },
          { title: 'Daily active token users tracked + shared', dueOffsetDays: 5, completed: false, owner: 'Product' },
        ],
      },
      {
        name: 'Adoption Push', days: 15, intensityStart: 70, intensityEnd: 60, pattern: 'linear', focusArea: 'partnerships',
        milestones: [
          { title: 'Usage-based rewards live',               dueOffsetDays: 2, completed: false, owner: 'Product' },
          { title: 'First Tier-2 CEX listing live',          dueOffsetDays: 5, completed: false, owner: 'BD' },
          { title: 'Integration partner #1 announced',       dueOffsetDays: 8, completed: false, owner: 'BD' },
          { title: '30-day adoption review (token-active users)', dueOffsetDays: 12, completed: false, owner: 'Product' },
        ],
      },
    ],
  },
  {
    id: 'token_launch',
    name: '30-Day Token Launch',
    description: 'Pre-launch hype → launch week → stabilization → growth',
    icon: Sparkles,
    accent: 'emerald',
    phases: [
      {
        name: 'Pre-Launch Hype', days: 7, intensityStart: 30, intensityEnd: 80, pattern: 'scurve', focusArea: 'marketing',
        milestones: [
          { title: 'Twitter announcement thread published',  dueOffsetDays: 0, completed: false, owner: 'Marketing' },
          { title: 'Discord server hits 5,000 members',      dueOffsetDays: 3, completed: false, owner: 'Community' },
          { title: 'Audit report published publicly',        dueOffsetDays: 5, completed: false, owner: 'Engineering' },
          { title: 'Tokenomics doc + whitepaper published',  dueOffsetDays: 6, completed: false, owner: 'Founders' },
        ],
      },
      {
        name: 'Launch Week', days: 7, intensityStart: 100, intensityEnd: 70, pattern: 'linear', focusArea: 'community',
        milestones: [
          { title: 'TGE: Token Generation Event live',       dueOffsetDays: 0, completed: false, owner: 'Engineering' },
          { title: 'DEX liquidity deployed (Uniswap V3)',    dueOffsetDays: 0, completed: false, owner: 'Treasury' },
          { title: 'AMA #1 hosted',                          dueOffsetDays: 1, completed: false, owner: 'Founders' },
          { title: 'First Tier-2 CEX listing live',          dueOffsetDays: 3, completed: false, owner: 'BD' },
          { title: 'Press release distributed',              dueOffsetDays: 4, completed: false, owner: 'Marketing' },
        ],
      },
      {
        name: 'Stabilization', days: 10, intensityStart: 60, intensityEnd: 50, pattern: 'linear', focusArea: 'community',
        milestones: [
          { title: 'AMA #2 hosted',                          dueOffsetDays: 2, completed: false, owner: 'Founders' },
          { title: 'CoinGecko + CoinMarketCap listings live', dueOffsetDays: 5, completed: false, owner: 'BD' },
          { title: 'Holder distribution review (top-10 < 50%)', dueOffsetDays: 7, completed: false, owner: 'Treasury' },
        ],
      },
      {
        name: 'Growth Phase', days: 6, intensityStart: 50, intensityEnd: 70, pattern: 'scurve', focusArea: 'partnerships',
        milestones: [
          { title: 'First partnership announced',            dueOffsetDays: 2, completed: false, owner: 'BD' },
          { title: 'Tier-1 CEX application submitted',       dueOffsetDays: 4, completed: false, owner: 'BD' },
        ],
      },
    ],
  },
  {
    id: 'listing_push',
    name: 'Tier-1 Listing Push',
    description: '28-day sprint toward Binance/Coinbase listing',
    icon: ShieldCheck,
    accent: 'blue',
    phases: [
      {
        name: 'Documentation', days: 7, intensityStart: 70, intensityEnd: 90, pattern: 'linear', focusArea: 'compliance',
        milestones: [
          { title: 'Updated audit report obtained',          dueOffsetDays: 2, completed: false, owner: 'Engineering' },
          { title: 'Legal opinion on token classification',  dueOffsetDays: 4, completed: false, owner: 'Legal' },
          { title: 'Founder KYC packets prepared',           dueOffsetDays: 5, completed: false, owner: 'Founders' },
          { title: 'Token risk-assessment doc prepared',     dueOffsetDays: 6, completed: false, owner: 'Founders' },
        ],
      },
      {
        name: 'Outreach', days: 7, intensityStart: 80, intensityEnd: 80, pattern: 'linear', focusArea: 'partnerships',
        milestones: [
          { title: 'Listing applications submitted to 3 CEXes', dueOffsetDays: 2, completed: false, owner: 'BD' },
          { title: 'Initial response from at least 1 CEX',   dueOffsetDays: 5, completed: false, owner: 'BD' },
        ],
      },
      {
        name: 'Due Diligence', days: 7, intensityStart: 90, intensityEnd: 70, pattern: 'linear', focusArea: 'compliance',
        milestones: [
          { title: 'Technical due diligence call completed', dueOffsetDays: 2, completed: false, owner: 'Engineering' },
          { title: 'Business due diligence call completed',  dueOffsetDays: 4, completed: false, owner: 'Founders' },
          { title: 'Listing fee + market-maker terms agreed', dueOffsetDays: 6, completed: false, owner: 'Founders' },
        ],
      },
      {
        name: 'Launch', days: 7, intensityStart: 100, intensityEnd: 60, pattern: 'linear', focusArea: 'marketing',
        milestones: [
          { title: 'Listing announcement coordinated',       dueOffsetDays: 0, completed: false, owner: 'Marketing' },
          { title: 'Trading pairs live on CEX',              dueOffsetDays: 1, completed: false, owner: 'BD' },
          { title: 'Post-listing AMA hosted',                dueOffsetDays: 2, completed: false, owner: 'Founders' },
        ],
      },
    ],
  },
  {
    id: 'community_growth',
    name: 'Community Growth Sprint',
    description: '21-day phased push: awareness → engagement → retention',
    icon: Users,
    accent: 'violet',
    phases: [
      {
        name: 'Awareness', days: 7, intensityStart: 50, intensityEnd: 80, pattern: 'scurve', focusArea: 'marketing',
        milestones: [
          { title: 'KOL partnerships signed (3-5)',          dueOffsetDays: 2, completed: false, owner: 'Marketing' },
          { title: 'Paid Twitter campaign launched',         dueOffsetDays: 3, completed: false, owner: 'Marketing' },
          { title: 'YouTube content sponsorships',           dueOffsetDays: 5, completed: false, owner: 'Marketing' },
        ],
      },
      {
        name: 'Engagement', days: 7, intensityStart: 80, intensityEnd: 80, pattern: 'linear', focusArea: 'community',
        milestones: [
          { title: 'Discord events hosted (≥ 3)',            dueOffsetDays: 2, completed: false, owner: 'Community' },
          { title: 'Twitter Spaces hosted (≥ 2)',            dueOffsetDays: 4, completed: false, owner: 'Community' },
          { title: 'Quest/contest campaign live (Galxe)',    dueOffsetDays: 5, completed: false, owner: 'Community' },
        ],
      },
      {
        name: 'Retention', days: 7, intensityStart: 70, intensityEnd: 50, pattern: 'linear', focusArea: 'community',
        milestones: [
          { title: 'Loyalty rewards program launched',       dueOffsetDays: 2, completed: false, owner: 'Community' },
          { title: 'Holder analysis report (week-over-week)', dueOffsetDays: 5, completed: false, owner: 'Treasury' },
        ],
      },
    ],
  },
  {
    id: 'liquidity_migration',
    name: 'Liquidity Migration',
    description: '14-day phased LP move across chains/pools',
    icon: Wallet,
    accent: 'orange',
    phases: [
      {
        name: 'Snapshot', days: 3, intensityStart: 30, intensityEnd: 50, pattern: 'linear', focusArea: 'treasury',
        milestones: [
          { title: 'Current LP positions documented',        dueOffsetDays: 0, completed: false, owner: 'Treasury' },
          { title: 'Target pool depths computed (DEX Liquidity Optimizer)', dueOffsetDays: 1, completed: false, owner: 'Treasury' },
          { title: 'Slippage tolerance + migration sequence approved', dueOffsetDays: 2, completed: false, owner: 'Founders' },
        ],
      },
      {
        name: 'Drawdown', days: 5, intensityStart: 80, intensityEnd: 60, pattern: 'linear', focusArea: 'treasury',
        milestones: [
          { title: 'Old LP positions withdrawn (50%)',       dueOffsetDays: 1, completed: false, owner: 'Treasury' },
          { title: 'Old LP positions fully withdrawn',       dueOffsetDays: 3, completed: false, owner: 'Treasury' },
        ],
      },
      {
        name: 'Redeploy', days: 6, intensityStart: 60, intensityEnd: 90, pattern: 'scurve', focusArea: 'treasury',
        milestones: [
          { title: 'New pool deployed (target tightness range)', dueOffsetDays: 1, completed: false, owner: 'Treasury' },
          { title: 'Initial liquidity added',                dueOffsetDays: 2, completed: false, owner: 'Treasury' },
          { title: 'Migration announcement published',       dueOffsetDays: 3, completed: false, owner: 'Marketing' },
          { title: 'Volume rebalance verified (Volume Health)', dueOffsetDays: 5, completed: false, owner: 'Treasury' },
        ],
      },
    ],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function interpolate(phase, dayIndex) {
  const { intensityStart, intensityEnd, days, pattern = 'linear' } = phase;
  if (days <= 1) return Math.round(intensityStart);
  const p = dayIndex / (days - 1);
  if (pattern === 'exponential') {
    const s = Math.max(intensityStart, 1), e = Math.max(intensityEnd, 1);
    return Math.round(s * Math.pow(e / s, p));
  }
  if (pattern === 'scurve') {
    const sp = 1 / (1 + Math.exp(-10 * (p - 0.5)));
    return Math.round(intensityStart + (intensityEnd - intensityStart) * sp);
  }
  return Math.round(intensityStart + (intensityEnd - intensityStart) * p);
}

function buildChartData(phases) {
  const data = [];
  let dayNum = 0;
  let phaseStartOffset = 0;
  phases.forEach((phase, pi) => {
    for (let d = 0; d < phase.days; d++) {
      data.push({
        day: ++dayNum,
        intensity: interpolate(phase, d),
        phase: phase.name,
        focusArea: phase.focusArea,
        phaseIdx: pi,
        milestoneCount: (phase.milestones || []).filter(m => Number(m.dueOffsetDays) === d).length,
      });
    }
    phaseStartOffset += phase.days;
  });
  return data;
}

function fmtDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ─── Premium hero ────────────────────────────────────────────────────────────

function Hero({ onRefresh, refreshing }) {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-rose-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-rose-500/20 p-6">
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-rose-400/10 blur-3xl pointer-events-none" />
      <div className="relative flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-rose-500/20 border border-rose-400/30 flex items-center justify-center flex-shrink-0">
            <MapIcon className="w-6 h-6 text-rose-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">Launch Roadmap Planner</h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-mono">Real milestones</span>
            </div>
            <p className="text-xs text-rose-200/70 mt-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Elite tier · Plan token launches across phased timelines with real milestone tracking
              <InfoTip side="bottom">Intensity curves are for RESOURCING planning (when to hire / push / rest), NOT trade execution — no bot fires at intensity 100. Always assign a named owner per milestone.</InfoTip>
            </p>
          </div>
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-rose-400/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 disabled:opacity-50 text-xs font-medium"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        )}
      </div>
    </div>
  );
}

// ─── About card (collapsible explainer) ─────────────────────────────────────

function AboutCard({ open, onToggle }) {
  return (
    <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 overflow-hidden">
      <button onClick={onToggle} className="w-full px-5 py-4 flex items-center justify-between hover:bg-dark-700/30 transition">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-rose-400" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-white">What is this tool — and how do I use it?</div>
            <div className="text-[11px] text-gray-500">Plain-English walkthrough · 30 seconds</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-dark-700/40 pt-4 space-y-4">
          <div>
            <div className="text-xs font-semibold text-rose-300 uppercase tracking-wider mb-1.5">The problem</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              Token launches have too many moving parts. Marketing campaigns, audits, CEX outreach, community events, treasury moves — each on its own clock, with different owners. Most teams track this in scattered docs and forget half of it under launch-week pressure.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-rose-300 uppercase tracking-wider mb-1.5">What this tool does</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              Plan your launch as <span className="text-white">phased timelines</span> with real milestones. Each phase has a focus area (marketing, product, compliance…), an effort intensity curve, and trackable milestones with owners and dates. You see the whole plan at a glance, check items off as you go, and see what's overdue or coming up next.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-rose-300 uppercase tracking-wider mb-1.5">How to use it in 3 steps</div>
            <ol className="text-sm text-gray-300 leading-relaxed space-y-1.5 list-decimal pl-5">
              <li>Click <span className="text-white">New roadmap</span> and pick a <span className="text-white">template</span> closest to your launch — Utility, 30-Day, Tier-1 Listing, Community, Liquidity Migration — or start blank.</li>
              <li>Adjust phases, milestones, and owners to match your team. Set a <span className="text-white">start date</span>.</li>
              <li>As you execute, tick milestones off. The progress bar and "what's next" callout keep you focused.</li>
            </ol>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-200/90 leading-relaxed">
              <span className="font-semibold">Important note:</span> The "intensity curve" represents % of team effort directed at each phase's focus area — a resourcing planning aid, NOT a market-manipulation signal. The previous tool here was a wash-trading planner and was removed for regulatory reasons (coordinated multi-wallet trading is market manipulation under SEC / CFTC / MiCA).
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function LaunchRoadmapPlannerPage() {
  const [view, setView] = useState('list'); // 'list' | 'templates' | 'builder' | 'detail'
  const [roadmaps, setRoadmaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [draftPhases, setDraftPhases] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(true);

  async function load() {
    setRefreshing(true);
    try {
      const data = await listRoadmaps();
      setRoadmaps(data?.roadmaps || []);
    } catch (e) {
      setError(e?.message || 'Failed to load roadmaps');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }
  useEffect(() => { load(); }, []);

  function onPickTemplate(template) {
    setDraftPhases(JSON.parse(JSON.stringify(template.phases)));
    setView('builder');
  }
  function onPickCustom() {
    setDraftPhases([{
      name: 'Phase 1', days: 7, intensityStart: 50, intensityEnd: 80, pattern: 'scurve', focusArea: 'marketing',
      milestones: [{ title: 'First milestone', dueOffsetDays: 0, completed: false, owner: 'Founders' }],
    }]);
    setView('builder');
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <Hero onRefresh={view === 'list' ? load : null} refreshing={refreshing} />

      {/* What is this tool? — collapsible explainer, only on list view */}
      {view === 'list' && (
        <AboutCard open={aboutOpen} onToggle={() => setAboutOpen(o => !o)} />
      )}

      {error && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3 text-xs text-red-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {view === 'list' && (
        <RoadmapList
          roadmaps={roadmaps}
          loading={loading}
          onCreate={() => setView('templates')}
          onSelect={(r) => { setSelected(r); setView('detail'); }}
        />
      )}

      {view === 'templates' && (
        <TemplatesView onPick={onPickTemplate} onCustom={onPickCustom} onBack={() => setView('list')} />
      )}

      {view === 'builder' && draftPhases && (
        <Builder
          initialPhases={draftPhases}
          onCancel={() => setView('templates')}
          onCreated={(r) => { setSelected(r); setRoadmaps(prev => [r, ...prev]); setView('detail'); }}
        />
      )}

      {view === 'detail' && selected && (
        <DetailView
          roadmap={selected}
          onBack={() => { setSelected(null); setView('list'); }}
          onUpdated={(r) => { setSelected(r); setRoadmaps(prev => prev.map(x => x.roadmapId === r.roadmapId ? r : x)); }}
          onDeleted={(id) => { setRoadmaps(prev => prev.filter(x => x.roadmapId !== id)); setSelected(null); setView('list'); }}
        />
      )}
    </div>
  );
}

// ─── List view ──────────────────────────────────────────────────────────────

function RoadmapList({ roadmaps, loading, onCreate, onSelect }) {
  if (loading) {
    return (
      <div className="bg-dark-800/40 rounded-2xl border border-dark-700/40 p-10 text-center">
        <Loader2 className="w-6 h-6 text-rose-400 mx-auto animate-spin" />
      </div>
    );
  }
  if (!roadmaps || roadmaps.length === 0) {
    return (
      <div className="bg-dark-800/40 rounded-2xl border border-dark-700/40 border-dashed p-12 text-center">
        <MapIcon className="w-12 h-12 text-rose-400/40 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-white mb-1">No roadmaps yet</h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
          Plan your token launch as phased milestones. Pick a template (Token Launch / Listing Push / Community Growth / Liquidity Migration) or build a custom roadmap.
        </p>
        <button onClick={onCreate} className="px-5 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-bold hover:bg-rose-400">
          <Plus className="w-4 h-4 inline mr-1" /> Create your first roadmap
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{roadmaps.length} roadmap{roadmaps.length !== 1 ? 's' : ''}</p>
        <button onClick={onCreate} className="px-3 py-1.5 rounded-lg bg-rose-500/10 border border-rose-400/30 text-rose-300 text-xs font-semibold hover:bg-rose-500/20">
          <Plus className="w-3.5 h-3.5 inline mr-1" /> New roadmap
        </button>
      </div>
      {roadmaps.map(r => {
        const pct = r.summary?.totalMilestones > 0
          ? Math.round((r.summary.completedMilestones / r.summary.totalMilestones) * 100)
          : 0;
        return (
          <button
            key={r.roadmapId}
            onClick={() => onSelect(r)}
            className="w-full text-left bg-dark-800/60 hover:bg-dark-800/80 border border-dark-700/50 hover:border-rose-500/30 rounded-2xl p-5 transition"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 border border-rose-400/20 flex items-center justify-center flex-shrink-0">
                <MapIcon className="w-5 h-5 text-rose-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold truncate">{r.name}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {r.phases?.length || 0} phase{(r.phases?.length || 0) !== 1 ? 's' : ''} · {r.summary?.totalDays || 0} days · starts {fmtDate(r.startDate)}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-dark-700 rounded-full overflow-hidden max-w-xs">
                    <div className="h-full bg-rose-400 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[10px] text-gray-500 font-mono">{r.summary?.completedMilestones || 0}/{r.summary?.totalMilestones || 0} milestones</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Templates view ─────────────────────────────────────────────────────────

function TemplatesView({ onPick, onCustom, onBack }) {
  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-white flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h2 className="text-xl font-bold text-white">Pick a template or start fresh</h2>
      <p className="text-sm text-gray-500">Each template is a realistic phased plan. You can customize every phase and milestone after.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {TEMPLATES.map(t => {
          const Icon = t.icon;
          const milestoneCount = t.phases.reduce((s, p) => s + p.milestones.length, 0);
          return (
            <button key={t.id} onClick={() => onPick(t)} className="text-left bg-dark-800/60 border border-dark-700/50 hover:border-rose-500/30 rounded-2xl p-5 transition">
              <div className={`w-10 h-10 rounded-xl bg-${t.accent}-500/10 border border-${t.accent}-500/20 flex items-center justify-center mb-3`}>
                <Icon className={`w-5 h-5 text-${t.accent}-400`} />
              </div>
              <h3 className="text-white font-semibold">{t.name}</h3>
              <p className="text-xs text-gray-500 mt-1">{t.description}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {t.phases.map((p, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-dark-700 text-gray-400 font-mono">
                    {p.name} · {p.days}d
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-gray-600 mt-3">{t.phases.length} phases · {milestoneCount} milestones · {t.phases.reduce((s,p)=>s+p.days,0)} days</p>
            </button>
          );
        })}
        <button onClick={onCustom} className="text-left bg-dark-800/40 border border-dark-700/50 border-dashed hover:border-rose-500/30 rounded-2xl p-5 transition flex flex-col items-center justify-center text-center min-h-[200px]">
          <Plus className="w-8 h-8 text-gray-600 mb-2" />
          <h3 className="text-white font-semibold">Custom roadmap</h3>
          <p className="text-xs text-gray-500 mt-1">Build phases + milestones from scratch</p>
        </button>
      </div>
    </div>
  );
}

// ─── Builder view ───────────────────────────────────────────────────────────

function Builder({ initialPhases, onCancel, onCreated }) {
  const [phases, setPhases] = useState(initialPhases);
  const [name, setName] = useState('');
  const [tokenAddress, setTokenAddress] = useState('');
  const [network, setNetwork] = useState('ethereum');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const totalDays = phases.reduce((s, p) => s + (p.days || 0), 0);
  const totalMilestones = phases.reduce((s, p) => s + (p.milestones?.length || 0), 0);
  const chartData = useMemo(() => buildChartData(phases), [phases]);

  function updatePhase(idx, patch) {
    setPhases(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  }
  function addPhase() {
    setPhases(prev => [...prev, {
      name: `Phase ${prev.length + 1}`, days: 7, intensityStart: 50, intensityEnd: 70, pattern: 'linear', focusArea: 'marketing',
      milestones: [],
    }]);
  }
  function removePhase(idx) {
    if (phases.length <= 1) return;
    setPhases(prev => prev.filter((_, i) => i !== idx));
  }
  function addMilestone(phaseIdx) {
    setPhases(prev => prev.map((p, i) => i === phaseIdx
      ? { ...p, milestones: [...(p.milestones || []), { title: 'New milestone', dueOffsetDays: 0, completed: false, owner: '' }] }
      : p));
  }
  function removeMilestone(phaseIdx, msIdx) {
    setPhases(prev => prev.map((p, i) => i === phaseIdx
      ? { ...p, milestones: (p.milestones || []).filter((_, mi) => mi !== msIdx) }
      : p));
  }
  function updateMilestone(phaseIdx, msIdx, patch) {
    setPhases(prev => prev.map((p, i) => i === phaseIdx
      ? { ...p, milestones: p.milestones.map((m, mi) => mi === msIdx ? { ...m, ...patch } : m) }
      : p));
  }

  async function save() {
    if (!name.trim()) { setError('Roadmap name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const r = await createRoadmap({ name: name.trim(), tokenAddress, network, startDate, phases });
      onCreated(r);
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <button onClick={onCancel} className="text-sm text-gray-500 hover:text-white flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to templates
      </button>

      <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Roadmap details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-[10px] uppercase text-gray-500 mb-1.5">Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Q2 token launch"
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:border-rose-400/40 focus:outline-none" />
          </div>
          <div>
            <label className="block text-[10px] uppercase text-gray-500 mb-1.5">Network</label>
            <select value={network} onChange={e => setNetwork(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:border-rose-400/40 focus:outline-none">
              {[
                { id: 'ethereum',  label: 'Ethereum' },
                { id: 'base',      label: 'Base' },
                { id: 'arbitrum',  label: 'Arbitrum' },
                { id: 'optimism',  label: 'Optimism' },
                { id: 'polygon',   label: 'Polygon' },
                { id: 'bsc',       label: 'BNB Chain' },
                { id: 'avalanche', label: 'Avalanche' },
                { id: 'blast',     label: 'Blast' },
                { id: 'linea',     label: 'Linea' },
                { id: 'scroll',    label: 'Scroll' },
              ].map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase text-gray-500 mb-1.5">Token address (optional)</label>
            <input type="text" value={tokenAddress} onChange={e => setTokenAddress(e.target.value)} placeholder="0x..."
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-rose-400/40 focus:outline-none" />
          </div>
          <div>
            <label className="block text-[10px] uppercase text-gray-500 mb-1.5">Start date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:border-rose-400/40 focus:outline-none" />
          </div>
        </div>
      </div>

      {/* Live intensity preview chart */}
      <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Intensity curve</h3>
          <p className="text-[10px] text-gray-500">{totalDays} days · {phases.length} phases · {totalMilestones} milestones</p>
        </div>
        <p className="text-xs text-gray-500 mb-3">Intensity = % of team effort directed at each phase's focus area. Used for planning resourcing, not for coordinating trades.</p>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="intGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fb7185" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#fb7185" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px' }}
                formatter={(v, n, p) => [v + '%', p?.payload?.phase || '']} />
              <Area type="monotone" dataKey="intensity" stroke="#fb7185" strokeWidth={2} fill="url(#intGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Phases */}
      <div className="space-y-3">
        {phases.map((phase, idx) => (
          <PhaseEditor
            key={idx}
            idx={idx}
            phase={phase}
            onChange={(patch) => updatePhase(idx, patch)}
            onRemove={() => removePhase(idx)}
            removable={phases.length > 1}
            onAddMilestone={() => addMilestone(idx)}
            onRemoveMilestone={(mi) => removeMilestone(idx, mi)}
            onUpdateMilestone={(mi, patch) => updateMilestone(idx, mi, patch)}
          />
        ))}
        <button onClick={addPhase} className="w-full px-4 py-3 rounded-xl border border-dashed border-dark-600 text-gray-400 hover:border-rose-500/30 hover:text-white text-sm">
          <Plus className="w-4 h-4 inline mr-1" /> Add phase
        </button>
      </div>

      <div className="flex justify-end gap-3">
        {error && <span className="text-xs text-red-300 self-center">{error}</span>}
        <button onClick={onCancel} className="px-5 py-2.5 rounded-xl text-sm text-gray-400 border border-dark-600 hover:text-white">
          Cancel
        </button>
        <button onClick={save} disabled={saving || !name.trim()} className="px-5 py-2.5 rounded-xl text-sm font-bold bg-rose-500 text-white hover:bg-rose-400 disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 inline mr-1 animate-spin" /> : null} Save roadmap
        </button>
      </div>
    </div>
  );
}

// ─── Phase Editor ───────────────────────────────────────────────────────────

function PhaseEditor({ idx, phase, onChange, onRemove, removable, onAddMilestone, onRemoveMilestone, onUpdateMilestone }) {
  const focusConfig = FOCUS_AREAS[phase.focusArea] || FOCUS_AREAS.marketing;
  const Icon = focusConfig.icon;
  return (
    <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 overflow-hidden">
      <div className="p-4 border-b border-dark-700/50 flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div className={`w-9 h-9 rounded-lg bg-${focusConfig.color}-500/10 border border-${focusConfig.color}-500/30 flex items-center justify-center`}>
            <Icon className={`w-4 h-4 text-${focusConfig.color}-400`} />
          </div>
          <input
            type="text"
            value={phase.name}
            onChange={e => onChange({ name: e.target.value })}
            className="bg-transparent text-white font-semibold text-sm outline-none border-b border-transparent focus:border-rose-500/40 flex-1 max-w-xs"
          />
        </div>
        {removable && (
          <button onClick={onRemove} className="text-gray-600 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
        )}
      </div>
      <div className="p-4 space-y-4">
        {/* Phase config row */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Input label="Days" type="number" min={1} max={90} value={phase.days} onChange={v => onChange({ days: parseInt(v) || 1 })} />
          <Input label="Intensity start" type="number" min={0} max={100} value={phase.intensityStart} onChange={v => onChange({ intensityStart: parseInt(v) || 0 })} />
          <Input label="Intensity end" type="number" min={0} max={100} value={phase.intensityEnd} onChange={v => onChange({ intensityEnd: parseInt(v) || 0 })} />
          <div>
            <label className="block text-[10px] uppercase text-gray-500 mb-1.5">Pattern</label>
            <select value={phase.pattern} onChange={e => onChange({ pattern: e.target.value })}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-white text-xs focus:border-rose-400/40 focus:outline-none">
              {PATTERNS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase text-gray-500 mb-1.5">Focus area</label>
            <select value={phase.focusArea} onChange={e => onChange({ focusArea: e.target.value })}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-white text-xs focus:border-rose-400/40 focus:outline-none">
              {Object.entries(FOCUS_AREAS).map(([id, c]) => <option key={id} value={id}>{c.label}</option>)}
            </select>
          </div>
        </div>

        {/* Milestones */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">Milestones · {phase.milestones?.length || 0}</p>
            <button onClick={onAddMilestone} className="text-[11px] text-rose-300 hover:text-rose-200">
              <Plus className="w-3 h-3 inline mr-0.5" /> Add milestone
            </button>
          </div>
          <div className="space-y-2">
            {(phase.milestones || []).map((m, mi) => (
              <div key={mi} className="bg-dark-900/40 border border-dark-700/40 rounded-lg p-2.5">
                <div className="flex items-start gap-2">
                  <input
                    type="text"
                    value={m.title}
                    onChange={e => onUpdateMilestone(mi, { title: e.target.value })}
                    placeholder="Milestone description"
                    className="flex-1 bg-transparent text-white text-xs outline-none border-b border-transparent focus:border-rose-500/40"
                  />
                  <input
                    type="number"
                    min={0}
                    max={phase.days - 1}
                    value={m.dueOffsetDays}
                    onChange={e => onUpdateMilestone(mi, { dueOffsetDays: parseInt(e.target.value) || 0 })}
                    title="Day of phase (0 = first day)"
                    className="w-12 bg-dark-700 border border-dark-600 rounded px-1.5 py-0.5 text-white text-[10px] font-mono text-center focus:border-rose-400/40 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={m.owner || ''}
                    onChange={e => onUpdateMilestone(mi, { owner: e.target.value })}
                    placeholder="Owner"
                    className="w-24 bg-dark-700 border border-dark-600 rounded px-2 py-0.5 text-white text-[10px] focus:border-rose-400/40 focus:outline-none"
                  />
                  <button onClick={() => onRemoveMilestone(mi)} className="text-gray-600 hover:text-red-400">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase text-gray-500 mb-1.5">{label}</label>
      <input {...props} onChange={e => props.onChange?.(e.target.value)}
        className="w-full bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-white text-xs font-mono focus:border-rose-400/40 focus:outline-none" />
    </div>
  );
}

// ─── Detail view ────────────────────────────────────────────────────────────

function DetailView({ roadmap, onBack, onUpdated, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(null); // milestoneId being toggled

  async function handleDelete() {
    if (!window.confirm(`Delete roadmap "${roadmap.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteRoadmap(roadmap.roadmapId);
      onDeleted(roadmap.roadmapId);
    } catch (e) {
      alert(e?.message || 'Delete failed');
      setDeleting(false);
    }
  }
  async function handleToggle(phaseIdx, milestoneId) {
    setToggling(milestoneId);
    try {
      const updated = await toggleRoadmapMilestone(roadmap.roadmapId, phaseIdx, milestoneId);
      onUpdated(updated);
    } catch (e) {
      alert(e?.message || 'Toggle failed');
    } finally {
      setToggling(null);
    }
  }

  const chartData = useMemo(() => buildChartData(roadmap.phases || []), [roadmap]);
  const totalMs = roadmap.summary?.totalMilestones || 0;
  const doneMs = roadmap.summary?.completedMilestones || 0;
  const pct = totalMs > 0 ? Math.round((doneMs / totalMs) * 100) : 0;

  // Flatten all milestones with their absolute dates so we can surface
  // "what's overdue" + "what's coming up next" — the most useful at-a-glance
  // information for someone actively running a launch.
  const flatMilestones = useMemo(() => {
    const out = [];
    let phaseStartOffset = 0;
    const startMs = new Date(roadmap.startDate).getTime();
    (roadmap.phases || []).forEach((phase, phaseIdx) => {
      (phase.milestones || []).forEach(m => {
        const offset = Number(m.dueOffsetDays) || 0;
        const dueMs = startMs + (phaseStartOffset + offset) * 86400000;
        out.push({
          ...m, phaseIdx, phaseName: phase.name,
          focusArea: phase.focusArea, dueDate: new Date(dueMs).toISOString().slice(0, 10), dueMs,
        });
      });
      phaseStartOffset += phase.days || 0;
    });
    return out;
  }, [roadmap]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();
  const overdue = flatMilestones.filter(m => !m.completed && m.dueMs < todayMs).sort((a, b) => a.dueMs - b.dueMs);
  const upcoming = flatMilestones.filter(m => !m.completed && m.dueMs >= todayMs).sort((a, b) => a.dueMs - b.dueMs).slice(0, 5);
  const nextMilestone = upcoming[0];

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-white flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to roadmaps
      </button>

      <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-white">{roadmap.name}</h2>
            <p className="text-xs text-gray-500 mt-1">
              {roadmap.phases?.length || 0} phases · {roadmap.summary?.totalDays || 0} days · {totalMs} milestones · Starts {fmtDate(roadmap.startDate)}
            </p>
          </div>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 text-xs font-semibold disabled:opacity-40"
          >
            <Trash2 className="w-3.5 h-3.5 inline mr-1" /> {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 h-2 bg-dark-700 rounded-full overflow-hidden">
            <div className="h-full bg-rose-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs text-gray-400 font-mono">{doneMs}/{totalMs} · {pct}%</span>
        </div>
      </div>

      {/* What's next + overdue */}
      {(overdue.length > 0 || upcoming.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {overdue.length > 0 && (
            <div className="bg-gradient-to-br from-red-500/10 to-amber-500/5 border border-red-500/30 rounded-2xl p-5">
              <h3 className="text-sm font-bold text-red-300 flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" /> Overdue ({overdue.length})
              </h3>
              <p className="text-[11px] text-red-200/70 mb-3">These milestones passed their due date and aren't checked off yet.</p>
              <div className="space-y-1.5">
                {overdue.slice(0, 4).map(m => (
                  <div key={m.id} className="bg-dark-900/40 rounded-lg p-2.5 border border-red-500/15">
                    <p className="text-xs text-white font-medium">{m.title}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      Was due {fmtDate(m.dueDate)} · {m.phaseName}{m.owner ? ` · ${m.owner}` : ''}
                    </p>
                  </div>
                ))}
                {overdue.length > 4 && (
                  <p className="text-[10px] text-red-300/70 italic pl-1">+ {overdue.length - 4} more overdue</p>
                )}
              </div>
            </div>
          )}
          {upcoming.length > 0 && (
            <div className="bg-gradient-to-br from-rose-500/10 to-cyan-500/5 border border-rose-500/30 rounded-2xl p-5">
              <h3 className="text-sm font-bold text-rose-300 flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4" /> What's next
              </h3>
              {nextMilestone && (
                <p className="text-[11px] text-rose-200/80 mb-3">
                  Next up: <span className="text-white font-semibold">{nextMilestone.title}</span> on {fmtDate(nextMilestone.dueDate)}.
                </p>
              )}
              <div className="space-y-1.5">
                {upcoming.map(m => (
                  <div key={m.id} className="bg-dark-900/40 rounded-lg p-2.5 border border-rose-500/15">
                    <p className="text-xs text-white font-medium">{m.title}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      Due {fmtDate(m.dueDate)} · {m.phaseName}{m.owner ? ` · ${m.owner}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Intensity curve */}
      <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
        <h3 className="text-sm font-bold text-white mb-2">Intensity curve</h3>
        <p className="text-xs text-gray-500 mb-3">% of team effort directed at each phase's focus area.</p>
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="detGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fb7185" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#fb7185" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px' }}
                formatter={(v, n, p) => [v + '%', p?.payload?.phase || '']} />
              <Area type="monotone" dataKey="intensity" stroke="#fb7185" strokeWidth={2} fill="url(#detGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Phases + milestones */}
      <div className="space-y-3">
        {(roadmap.phases || []).map((phase, idx) => {
          const focus = FOCUS_AREAS[phase.focusArea] || FOCUS_AREAS.marketing;
          const Icon = focus.icon;
          const phaseDoneMs = (phase.milestones || []).filter(m => m.completed).length;
          const phaseTotalMs = (phase.milestones || []).length;
          return (
            <div key={idx} className="bg-dark-800/60 rounded-2xl border border-dark-700/50 overflow-hidden">
              <div className="p-4 border-b border-dark-700/50 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg bg-${focus.color}-500/10 border border-${focus.color}-500/30 flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 text-${focus.color}-400`} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">{phase.name}</p>
                    <p className="text-[10px] text-gray-500">{phase.days} days · {focus.label} focus · {phase.pattern} ramp</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-white font-mono">{phaseDoneMs}/{phaseTotalMs}</p>
                  <p className="text-[10px] text-gray-500">milestones</p>
                </div>
              </div>
              <div className="p-3 space-y-1.5">
                {(phase.milestones || []).length === 0 && (
                  <p className="text-[11px] text-gray-600 italic px-2 py-1">No milestones in this phase.</p>
                )}
                {(phase.milestones || []).map((m, mi) => (
                  <button
                    key={m.id || mi}
                    onClick={() => m.id && handleToggle(idx, m.id)}
                    disabled={!m.id || toggling === m.id}
                    className={`w-full text-left flex items-start gap-3 px-3 py-2 rounded-lg transition ${m.completed ? 'bg-emerald-500/5 border border-emerald-500/15' : 'hover:bg-dark-700/40 border border-transparent'}`}
                  >
                    {toggling === m.id
                      ? <Loader2 className="w-4 h-4 text-rose-400 animate-spin flex-shrink-0 mt-0.5" />
                      : m.completed
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                        : <Circle className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    }
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs ${m.completed ? 'text-emerald-300 line-through' : 'text-white'}`}>{m.title}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-2">
                        <Calendar className="w-2.5 h-2.5 inline" /> Day {m.dueOffsetDays + 1} of phase
                        {m.owner && <><span>·</span><span>{m.owner}</span></>}
                        {m.completedAt && <><span>·</span><span className="text-emerald-400">Completed {fmtDate(m.completedAt)}</span></>}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
